import React, { useState, useEffect } from 'react';
import { Save, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useSite } from '../../contexts/SiteContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminEmailSettings() {
  const { settings, updateSettings } = useSite();
  const [formData, setFormData] = useState({
    gmail_user: '',
    gmail_app_password: ''
  });
  const [saving, setSaving] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);

  useEffect(() => {
    setFormData({
      gmail_user: settings.gmail_user || '',
      gmail_app_password: ''
    });
    setPasswordSet(!!settings.gmail_app_password_set);
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSettings({
        ...settings,
        gmail_user: formData.gmail_user,
        gmail_app_password: formData.gmail_app_password
      });
      toast.success('Email settings saved');
      setFormData(prev => ({ ...prev, gmail_app_password: '' }));
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl" data-testid="admin-email-settings">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Email Configuration</h1>
        <p className="text-muted-foreground">Configure Gmail SMTP for contact form notifications</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="border border-border rounded-lg p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Gmail SMTP</h2>
          </div>

          <p className="text-sm text-muted-foreground">
            To receive contact form submissions via email, enter your Gmail credentials below.
            You'll need a{' '}
            <a
              href="https://myaccount.google.com/apppasswords"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Gmail App Password
            </a>{' '}
            (not your regular password).
          </p>

          <div className="space-y-2">
            <Label htmlFor="gmail_user">Gmail Address</Label>
            <Input
              id="gmail_user"
              name="gmail_user"
              type="email"
              value={formData.gmail_user}
              onChange={handleChange}
              placeholder="your-email@gmail.com"
              data-testid="email-gmail-user"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gmail_app_password">Gmail App Password</Label>
            <Input
              id="gmail_app_password"
              name="gmail_app_password"
              type="password"
              value={formData.gmail_app_password}
              onChange={handleChange}
              placeholder={passwordSet ? '(saved — leave blank to keep)' : 'Enter app password'}
              data-testid="email-gmail-password"
            />
            {passwordSet && (
              <p className="text-xs text-green-600 dark:text-green-400">
                App password is configured.
              </p>
            )}
          </div>
        </div>

        <Button type="submit" disabled={saving} data-testid="save-email-settings-btn">
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving...' : 'Save Email Settings'}
        </Button>
      </form>
    </div>
  );
}
